import { useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import BusinessListingApplicantsSection from "../../../components/business-listings/BusinessListingApplicantsSection";
import PageBackButton from "../../../components/ui/Page/PageBackButton";
import PageHero from "../../../components/ui/Page/PageHero";
import PageSectionCard from "../../../components/ui/Page/PageSectionCard";
import PageShell from "../../../components/ui/Page/PageShell";
import { businessListingsApi } from "../../../lib/businessListings";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import { formatBusinessListingCompensation, formatBusinessListingJobType } from "../../../utils/BusinessListingUtils";

const listingStatusLabel = {
  DRAFT: "Draft",
  PUBLISHED: "Published",
  EXPIRED: "Expired",
  CLOSED: "Closed",
} as const;

const BusinessListingApplicantsPage = () => {
  const { listingId } = useParams<{ listingId: string }>();
  const [currentTime] = useState(() => Date.now());

  const listingQuery = useQuery({
    queryKey: ["business-listing", listingId],
    queryFn: () => businessListingsApi.getById(listingId!),
    enabled: Boolean(listingId),
  });

  if (!listingId) {
    return <Navigate to="/adult/gigs" replace />;
  }

  const applicantActionsOpen = listingQuery.data
    ? listingQuery.data.status === "PUBLISHED"
      && listingQuery.data.expiresAt !== null
      && new Date(listingQuery.data.expiresAt).getTime() > currentTime
    : false;
  const listingStatusText = listingQuery.data
    ? (!applicantActionsOpen && listingQuery.data.status === "PUBLISHED"
      ? "Expired"
      : listingStatusLabel[listingQuery.data.status])
    : null;

  return (
    <PageShell width="6xl">
      <PageBackButton fallbackTo="/adult/gigs" label="Back to Job Listings" className="mb-4" />

      {listingQuery.isLoading ? (
        <PageSectionCard className="p-5">
          <p className="text-sm text-myteengig-text-muted">Loading listing details...</p>
        </PageSectionCard>
      ) : null}

      {listingQuery.error ? (
        <PageSectionCard className="border-rose-200 bg-rose-50/70 p-5">
          <p className="text-sm text-rose-700">
            {apiErrorToMessage(listingQuery.error, "Failed to load this job listing.")}
          </p>
        </PageSectionCard>
      ) : null}

      {listingQuery.data ? (
        <>
          <PageHero
            title={<span className="text-2xl sm:text-3xl">Applicants</span>}
            description={
              applicantActionsOpen
                ? `Review applicants for ${listingQuery.data.title}.`
                : `Review applicant details for ${listingQuery.data.title}. This listing is no longer active, so applicant actions are read-only.`
            }
            className="bg-gradient-to-br from-white via-myteengig-background to-white py-3 sm:py-4 lg:py-4"
            contentClassName="[&_h1]:mt-0 [&_h1+div]:mt-3"
            footer={(
              <div className="flex flex-wrap items-center justify-center gap-2 text-sm text-myteengig-text-muted">
                <span className="rounded-full border border-myteengig-border bg-white px-3 py-1 font-medium text-myteengig-text-primary">
                  {formatBusinessListingCompensation(
                    listingQuery.data.payType,
                    listingQuery.data.minPayAmountCents,
                    listingQuery.data.maxPayAmountCents,
                    listingQuery.data.jobType,
                  )}
                </span>
                <span className="rounded-full border border-myteengig-border bg-white px-3 py-1 font-medium text-myteengig-text-primary">
                  {formatBusinessListingJobType(listingQuery.data.jobType)} - {listingQuery.data.category}
                </span>
                <span className="rounded-full border border-myteengig-border bg-white px-3 py-1 font-medium text-myteengig-text-primary">
                  {listingStatusText}
                </span>
              </div>
            )}
          />

          <BusinessListingApplicantsSection
            listingId={listingId}
            listingTitle={listingQuery.data.title}
            applicantActionsOpen={applicantActionsOpen}
          />
        </>
      ) : null}
    </PageShell>
  );
};

export default BusinessListingApplicantsPage;
